import React, { useEffect, useState } from 'react';
import {
    Box, Container, Heading, Text, VStack, HStack, Button, SimpleGrid, Image, Badge, Icon, Flex,
    Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, Wrap, WrapItem, Tag,
} from '@chakra-ui/react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
    FiCheckCircle, FiClock, FiMapPin, FiPhone, FiTruck, FiDroplet, FiWind, FiShield,
    FiCalendar, FiMessageSquare, FiHeart, FiActivity, FiHome,
} from 'react-icons/fi';
import { smsBody, smsHref, textCode } from '../Components/SiteLanding/textOrderLinks';

const LAUNDRY_ID = '1';
const PHONE_DISPLAY = '(512) 782-0428';
const PHONE_TEL = '+15127820428';
const ADDRESS = '900 E Palm Valley Blvd, Suite 1006-1007, Round Rock, TX 78664';
const CANONICAL = 'https://roundrocklaundry.com/ozone-wash';

const IMG = (id, w) => `https://images.unsplash.com/${id}?w=${w}&q=75&auto=format&fit=crop`;
const HERO_IMG = 'photo-1517677208171-0bc6725a3e60';
const FOLD_IMG = 'photo-1582735689369-4fe89db7114c';

const AREAS = [
    'Round Rock', 'Brushy Creek', 'Teravista', 'Forest Creek', 'Paloma Lake', 'Avery Ranch',
    'Cedar Park', 'Georgetown', 'Hutto', 'Pflugerville', 'Wells Branch', 'North Austin',
];

const WHO = [
    { icon: FiHeart, title: 'After someone has been sick', text: 'Sheets, towels, and the clothes they lived in. Ozone wash is the load families ask for when a cold, flu, or stomach bug has been through the house.' },
    { icon: FiHome, title: 'Babies and little kids', text: 'Onesies, bibs, and the blanket that goes everywhere. Parents who think about germs want a wash that does more than smell like detergent.' },
    { icon: FiActivity, title: 'Gym clothes and towels', text: 'Sweat holds odor in synthetic fabric. Ozone goes after the smell and the bacteria that keep a gym bag from ever really coming clean.' },
    { icon: FiShield, title: 'People who think about germs', text: 'Shared towels, pet blankets, and everyday clothes. If “is this actually clean?” is the question, ozone wash is the answer we give.' },
];

const HOW = [
    { icon: FiDroplet, title: 'Cold water', text: 'Ozone does the cleaning in cold water. Colors hold up, and the wash does not depend on a long hot cycle.' },
    { icon: FiWind, title: 'Ozone in the water', text: 'Ozone is oxygen with an extra atom. In the wash it breaks down soils, bacteria, viruses, and odor that sit on the fabric.' },
    { icon: FiCheckCircle, title: 'Then it is just oxygen', text: 'When the cycle ends, ozone turns back into oxygen. It does not stay on the clothes as a chemical film.' },
];

const STEPS = [
    { icon: FiCalendar, title: 'Schedule', text: 'Book a pickup online, text us, or drop a bag anytime. The store is open 24/7.' },
    { icon: FiTruck, title: 'We pick it up', text: 'Leave the bag at the door. Add a note if you want fragrance-free detergent.' },
    { icon: FiDroplet, title: 'Ozone wash', text: 'Every wash-and-fold load is washed with ozone. Same price as wash and fold. No add-on.' },
    { icon: FiCheckCircle, title: 'Back to you', text: 'Dried, folded, and returned. Pickup at the store or delivery to your door.' },
];

const FAQS = [
    { q: 'What is ozone wash?', a: 'Ozone wash is how Eco Spin Round Rock Laundry cleans clothes. Ozone — a form of oxygen — is mixed into cold wash water. It breaks down soils, bacteria, viruses, and odor on the fabric, then turns back into oxygen.' },
    { q: 'Does ozone wash help with germs and viruses?', a: 'Yes. That is why we use it. Ozone in the wash water is there to break down bacteria, viruses, and odor on clothes, towels, and sheets. It is a laundry wash for households that care about germs. It is not a medical treatment.' },
    { q: 'Is ozone wash extra?', a: 'No. Every wash-and-fold load is an ozone wash. You pay the wash-and-fold price. There is no separate ozone fee.' },
    { q: 'Is it safe on clothes and skin?', a: 'Ozone finishes as oxygen, so it does not leave a chemical film on the fabric. Cold water is gentler on colors than a hot sanitize cycle. Ask for fragrance-free detergent if someone in the house has sensitive skin.' },
    { q: 'What should I send after an illness?', a: 'Sheets, pillowcases, towels, and the clothes the sick person wore. Put them in the bag and tell us in the order notes. We will ozone-wash that load.' },
    { q: 'Do you pick up and deliver?', a: 'Yes. Schedule a pickup in Round Rock and nearby neighborhoods and we bring the folded clothes back. The delivery fee depends on distance and is shown before you confirm. You can also drop off at 900 E Palm Valley Blvd anytime.' },
];

const ACCENT = 'teal';
const SOLID = { bg: 'teal.600', color: 'white', _hover: { bg: 'teal.700', transform: 'translateY(-1px)', boxShadow: 'lg' } };

const TextToSchedule = ({ ordering, color = 'gray.700', iconColor = 'teal.500', testId }) => {
    if (!ordering?.keyword || !ordering?.smsNumber) return null;
    const code = textCode(ordering);
    return (
        <HStack as="a" href={smsHref(ordering.smsNumber, code)} spacing={1.5} color={color}
            data-testid={testId} _hover={{ textDecoration: 'underline' }}>
            <Icon as={FiMessageSquare} color={iconColor} />
            <Text fontSize="sm">
                Text to schedule — text{' '}
                <Text as="span" fontWeight="800" fontFamily="mono">{smsBody(code).trim()}</Text>
                {' '}to {ordering.smsNumberDisplay}
            </Text>
        </HStack>
    );
};

const SectionTitle = ({ eyebrow, title, sub }) => (
    <VStack spacing={3} textAlign="center" maxW="760px" mx="auto">
        {eyebrow && (
            <Text fontSize="sm" fontWeight="700" letterSpacing="0.12em" textTransform="uppercase" color={`${ACCENT}.600`}>
                {eyebrow}
            </Text>
        )}
        <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }} color="gray.800" lineHeight="short">{title}</Heading>
        {sub && <Text color="gray.600" fontSize={{ base: 'md', md: 'lg' }}>{sub}</Text>}
    </VStack>
);

const OzoneWashPage = () => {
    const params = useParams();
    const laundryId = params.laundryId || LAUNDRY_ID;
    const orderHref = `/${laundryId}`;
    const [textOrdering, setTextOrdering] = useState(null);

    useEffect(() => {
        let cancelled = false;
        axios.get(`${process.env.REACT_APP_AWS_API_URL}/api/laundry/get-info`, {
            params: { operation: 'getLaundryInfo', laundryId, isCustomer: true },
        }).then((res) => {
            if (!cancelled && res.data?.textOrdering?.keyword) setTextOrdering(res.data.textOrdering);
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [laundryId]);

    const jsonLd = [
        {
            '@context': 'https://schema.org',
            '@type': 'Service',
            serviceType: 'Ozone laundry wash',
            name: 'Ozone Wash in Round Rock, TX',
            description: 'Cold-water ozone wash at Eco Spin Round Rock Laundry, built to reduce germs, bacteria, and viruses on clothes. Included with wash and fold.',
            provider: {
                '@type': 'LocalBusiness',
                name: 'Eco Spin Round Rock Laundry',
                telephone: PHONE_TEL,
                address: {
                    '@type': 'PostalAddress',
                    streetAddress: '900 E Palm Valley Blvd, Suite 1006-1007',
                    addressLocality: 'Round Rock',
                    addressRegion: 'TX',
                    postalCode: '78664',
                    addressCountry: 'US',
                },
            },
            areaServed: AREAS.map((name) => ({ '@type': 'Place', name })),
        },
        {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: FAQS.map((f) => ({
                '@type': 'Question',
                name: f.q,
                acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
        },
    ];

    return (
        <Box bg="white" minH="100vh">
            <Helmet>
                <title>Ozone Wash in Round Rock, TX | Eco Spin Laundry</title>
                <meta
                    name="description"
                    content="Ozone wash at Eco Spin Round Rock Laundry. Cold-water ozone cleaning built to reduce germs, bacteria, and viruses on clothes. Pickup and delivery or 24/7 drop-off."
                />
                <link rel="canonical" href={CANONICAL} />
                <meta property="og:title" content="Ozone Wash in Round Rock, TX | Eco Spin Laundry" />
                <meta property="og:description" content="Cold-water ozone wash for households that care about germs and viruses. Included with every wash-and-fold load." />
                <meta property="og:image" content={IMG(HERO_IMG, 1200)} />
                <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
            </Helmet>

            <Box bg="teal.800" color="white" py={2.5} px={4} textAlign="center" data-testid="ozone-banner">
                <Text fontSize={{ base: 'sm', md: 'md' }}>
                    <Text as="span" fontWeight="800">Ozone wash on every load.</Text>{' '}
                    Built for germs, bacteria, and viruses — included with wash and fold.
                </Text>
            </Box>

            <Box position="relative" overflow="hidden" bg="linear-gradient(135deg, #F0FDFA 0%, #E6FFFA 45%, #F7FAFC 100%)">
                <Container maxW="1200px" py={{ base: 12, md: 20 }} position="relative">
                    <Flex direction={{ base: 'column', md: 'row' }} align="center" gap={{ base: 10, md: 14 }}>
                        <VStack align={{ base: 'center', md: 'flex-start' }} spacing={6} flex="1" textAlign={{ base: 'center', md: 'left' }}>
                            <Badge colorScheme={ACCENT} fontSize="sm" px={3} py={1} borderRadius="full" textTransform="none">
                                Round Rock ozone wash
                            </Badge>
                            <Heading as="h1" fontSize={{ base: '4xl', md: '6xl' }} lineHeight="1.02" color="gray.900" letterSpacing="-0.03em">
                                Clothes washed for people who care about{' '}
                                <Text as="span" color={`${ACCENT}.600`}>germs and viruses.</Text>
                            </Heading>
                            <Text fontSize={{ base: 'md', md: 'xl' }} color="gray.600" maxW="560px">
                                Every wash-and-fold load at Eco Spin Round Rock Laundry is an ozone wash.
                                Cold water, ozone in the cycle, and clothes that come back without a chemical film.
                            </Text>
                            <VStack spacing={3} pt={1} align={{ base: 'center', md: 'flex-start' }}>
                                <HStack spacing={4} flexWrap="wrap" justify={{ base: 'center', md: 'flex-start' }}>
                                    <Button as="a" href={orderHref} size="lg" {...SOLID} borderRadius="full" px={9} boxShadow="lg" data-testid="ozone-cta-hero">
                                        Schedule a Pickup
                                    </Button>
                                    <Button as="a" href="#how-it-works" size="lg" variant="ghost" colorScheme={ACCENT} borderRadius="full" px={6}>
                                        How ozone works
                                    </Button>
                                </HStack>
                                <TextToSchedule ordering={textOrdering} testId="ozone-text-hero" />
                            </VStack>
                            <HStack spacing={6} pt={1} color="gray.600" fontSize="sm" flexWrap="wrap" justify={{ base: 'center', md: 'flex-start' }}>
                                <HStack spacing={2}><Icon as={FiShield} color={`${ACCENT}.500`} /><Text>Germs, bacteria, viruses</Text></HStack>
                                <HStack spacing={2}><Icon as={FiClock} color={`${ACCENT}.500`} /><Text>Open 24/7 for drop-off</Text></HStack>
                                <HStack spacing={2}><Icon as={FiTruck} color={`${ACCENT}.500`} /><Text>Pickup & delivery</Text></HStack>
                            </HStack>
                        </VStack>
                        <Box flex="1" maxW="520px" w="100%">
                            <Image
                                src={IMG(HERO_IMG, 1000)}
                                alt="Folded clean laundry"
                                borderRadius="3xl"
                                boxShadow="2xl"
                                objectFit="cover"
                                w="100%"
                                h={{ base: '260px', md: '420px' }}
                            />
                        </Box>
                    </Flex>
                </Container>
            </Box>

            <Container maxW="1200px" py={{ base: 16, md: 24 }}>
                <SectionTitle
                    eyebrow="Who it is for"
                    title="The wash people choose when germs matter"
                    sub="Regular detergent gets clothes visually clean. Ozone is in the water because families also want what they cannot see — germs, bacteria, and leftover odor — dealt with."
                />
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mt={12}>
                    {WHO.map((c) => (
                        <HStack key={c.title} align="flex-start" spacing={4} p={6} borderRadius="2xl" bg="white"
                            border="1px solid" borderColor="gray.100" boxShadow="sm">
                            <Flex flexShrink={0} w={12} h={12} align="center" justify="center" borderRadius="full" bg={`${ACCENT}.50`} color={`${ACCENT}.600`}>
                                <Icon as={c.icon} boxSize={6} />
                            </Flex>
                            <Box>
                                <Heading as="h3" size="sm" mb={1.5} color="gray.800">{c.title}</Heading>
                                <Text color="gray.600" fontSize="sm">{c.text}</Text>
                            </Box>
                        </HStack>
                    ))}
                </SimpleGrid>
            </Container>

            <Box id="how-it-works" bg="gray.50" py={{ base: 16, md: 20 }}>
                <Container maxW="1200px">
                    <SectionTitle
                        eyebrow="How ozone wash works"
                        title="Oxygen in the water. Nothing left on the clothes."
                        sub="Ozone is not a scent and it is not a bleach soak. It is mixed into the wash, does the work, and breaks down."
                    />
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mt={12}>
                        {HOW.map((c) => (
                            <Box key={c.title} p={7} borderRadius="2xl" bg="white" border="1px solid" borderColor="gray.100">
                                <Flex w={12} h={12} align="center" justify="center" borderRadius="xl" bg={`${ACCENT}.50`} color={`${ACCENT}.600`} mb={4}>
                                    <Icon as={c.icon} boxSize={6} />
                                </Flex>
                                <Heading as="h3" size="md" mb={2} color="gray.800">{c.title}</Heading>
                                <Text color="gray.600" fontSize="sm">{c.text}</Text>
                            </Box>
                        ))}
                    </SimpleGrid>
                </Container>
            </Box>

            <Box bg="teal.800" color="white" py={{ base: 14, md: 16 }}>
                <Container maxW="900px" textAlign="center">
                    <Text fontSize="sm" fontWeight="700" letterSpacing="0.12em" textTransform="uppercase" color="teal.200">Included</Text>
                    <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }} mt={3}>No add-on. Ozone wash is the wash.</Heading>
                    <Text mt={4} fontSize={{ base: 'md', md: 'lg' }} color="teal.50">
                        Schedule wash and fold and this is how the clothes are cleaned. Same price. Pickup and delivery, or drop off at the store day or night.
                    </Text>
                    <Button as="a" href="/wash-and-fold" mt={6} size="lg" bg="white" color="teal.800" borderRadius="full" px={8}
                        _hover={{ bg: 'teal.50' }}>
                        Wash and fold details
                    </Button>
                </Container>
            </Box>

            <Container maxW="1200px" py={{ base: 16, md: 24 }}>
                <SectionTitle eyebrow="How to get it" title="Drop off a bag, or we come get it" />
                <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={8} mt={12}>
                    {STEPS.map((s, i) => (
                        <VStack key={s.title} spacing={3} textAlign="center" px={2}>
                            <Box position="relative">
                                <Flex w={16} h={16} align="center" justify="center" borderRadius="full" bg={`${ACCENT}.600`} color="white" boxShadow="lg">
                                    <Icon as={s.icon} boxSize={7} />
                                </Flex>
                                <Flex position="absolute" top={-1} right={-1} w={6} h={6} borderRadius="full" bg="white" border="2px solid"
                                    borderColor={`${ACCENT}.600`} align="center" justify="center" fontSize="xs" fontWeight="800" color={`${ACCENT}.700`}>
                                    {i + 1}
                                </Flex>
                            </Box>
                            <Heading as="h3" size="md" color="gray.800">{s.title}</Heading>
                            <Text color="gray.600" fontSize="sm">{s.text}</Text>
                        </VStack>
                    ))}
                </SimpleGrid>
            </Container>

            <Box bg="#F7FAFC" py={{ base: 16, md: 20 }}>
                <Container maxW="1200px">
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 10, md: 16 }} alignItems="center">
                        <Image src={IMG(FOLD_IMG, 900)} alt="Folded clean clothes stacked and ready" borderRadius="2xl" objectFit="cover" h={{ base: '240px', md: '380px' }} w="100%" boxShadow="lg" />
                        <VStack align="flex-start" spacing={5}>
                            <Text fontSize="sm" fontWeight="700" letterSpacing="0.12em" textTransform="uppercase" color={`${ACCENT}.600`}>What you get back</Text>
                            <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }} color="gray.800" lineHeight="short">
                                Clean, folded, and easier to trust
                            </Heading>
                            <VStack align="flex-start" spacing={3}>
                                {[
                                    'Washed for germs, bacteria, and odor — not just for looks',
                                    'Cold-water ozone, so colors are not beaten up by a hot cycle',
                                    'Fragrance-free on request, for sensitive skin',
                                    'Folded and packed so it goes straight into a drawer',
                                ].map((t) => (
                                    <HStack key={t} spacing={3} align="flex-start">
                                        <Icon as={FiCheckCircle} color={`${ACCENT}.500`} mt={1} />
                                        <Text color="gray.700">{t}</Text>
                                    </HStack>
                                ))}
                            </VStack>
                        </VStack>
                    </SimpleGrid>
                </Container>
            </Box>

            <Container maxW="1000px" py={{ base: 16, md: 20 }} textAlign="center">
                <SectionTitle
                    eyebrow="Service area"
                    title="Ozone wash across Round Rock and nearby"
                    sub="Pickup and delivery in Williamson County and North Austin. The delivery fee is shown before you confirm."
                />
                <Wrap justify="center" spacing={3} mt={8}>
                    {AREAS.map((a) => (
                        <WrapItem key={a}>
                            <Tag size="lg" borderRadius="full" variant="subtle" colorScheme={ACCENT} px={4} py={2}>
                                <Icon as={FiMapPin} mr={2} />{a}
                            </Tag>
                        </WrapItem>
                    ))}
                </Wrap>
            </Container>

            <Box bg="gray.50" py={{ base: 16, md: 20 }}>
                <Container maxW="820px">
                    <SectionTitle eyebrow="FAQ" title="Ozone wash questions" />
                    <Accordion allowToggle mt={10} bg="white" borderRadius="2xl" boxShadow="sm" overflow="hidden">
                        {FAQS.map((f) => (
                            <AccordionItem key={f.q} borderColor="gray.100">
                                <h3>
                                    <AccordionButton py={5} px={6} _hover={{ bg: 'gray.50' }}>
                                        <Box flex="1" textAlign="left" fontWeight="600" color="gray.800">{f.q}</Box>
                                        <AccordionIcon />
                                    </AccordionButton>
                                </h3>
                                <AccordionPanel px={6} pb={5} color="gray.600">{f.a}</AccordionPanel>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </Container>
            </Box>

            <Box bg="gray.900" color="white" py={{ base: 16, md: 20 }}>
                <Container maxW="800px" textAlign="center">
                    <VStack spacing={5}>
                        <Heading as="h2" fontSize={{ base: '3xl', md: '5xl' }} lineHeight="short">
                            Send the load you want actually clean
                        </Heading>
                        <Text color="gray.300" fontSize="lg" data-testid="ozone-footer-copy">
                            Text (512) 782-0428 to schedule laundry pickup in Round Rock, TX. Ozone wash is included.
                        </Text>
                        <VStack spacing={3}>
                            <HStack spacing={4} flexWrap="wrap" justify="center">
                                <Button as="a" href={orderHref} size="lg" {...SOLID} borderRadius="full" px={10} data-testid="ozone-cta-footer">
                                    Schedule a Pickup
                                </Button>
                                <Button as="a" href={`tel:${PHONE_TEL}`} size="lg" variant="outline" colorScheme="whiteAlpha" color="white" borderRadius="full" px={8} leftIcon={<FiPhone />}>
                                    {PHONE_DISPLAY}
                                </Button>
                            </HStack>
                            <TextToSchedule ordering={textOrdering} color="gray.200" iconColor="teal.300" testId="ozone-text-footer" />
                        </VStack>
                        <VStack spacing={1} pt={3} color="gray.400" fontSize="sm">
                            <HStack spacing={2}><Icon as={FiMapPin} /><Text>{ADDRESS}</Text></HStack>
                            <HStack spacing={2}><Icon as={FiClock} /><Text>Open 24/7</Text></HStack>
                        </VStack>
                    </VStack>
                </Container>
            </Box>
        </Box>
    );
};

export default OzoneWashPage;
